import { FormStateContext } from "@/components/bli-medlem/form-state-context";
import { SummarySkeleton } from "@/components/bli-medlem/summary/summary-skeleton";
import { PageContent } from "@/components/layout/page-content";
import { ErrorMessage, ErrorMessageFlavour } from "@/components/ui/error-message";
import type { OrderFragment, SubscriptionItem } from "@/generated/client.generated";
import { netsPaymentErrorsToString } from "@/utils/error-code-lookup";
import { Logger } from "@/utils/logger";
import { ROOT_URL } from "@/utils/paths-utils";
import storageUtils from "@/utils/storage-utils";
import dayjs from "dayjs";
import type Joi from "joi";
import { signIn, useSession } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import { parseAsString, useQueryState } from "nuqs";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { sendCheckoutEvent, sendStepEvent } from "../analytics";
import { type FormData, SessionStorage } from "../types";
import { Summary } from "./summary";
import {
  type UserInput,
  createOrder,
  createUser,
  fetchAccessToken,
  updateOrder,
  updateUser,
} from "./summary-server-functions";

const createOrUpdateUser = async (userInput: UserInput, accessTokenParam?: string, organizationId?: string) => {
  try {
    const accessToken = accessTokenParam ?? (await fetchAccessToken(userInput.email, userInput.password));
    //If user is logged in we update user
    const updatedUser = await updateUser(accessToken, userInput, organizationId);
    return {
      accessToken: accessToken,
      password: userInput.password,
      userId: updatedUser.userId,
    };
  } catch {
    const userData = await createUser(userInput, organizationId);
    const accessTokenResponse = await fetchAccessToken(userInput.email, userData.password);
    return {
      accessToken: accessTokenResponse,
      password: userData.password,
      userId: userData.userId,
    };
  }
};

const createOrderOnInitialLoad = async (formData: FormData, redirectUrl: string) => {
  if (!formData.mainCenter) {
    throw new Error("Primærsenter ikke valgt");
  }
  if (!formData.customerType) {
    throw new Error("Aldersgruppe ikke valgt");
  }
  if (!formData.accountType) {
    throw new Error("Medlemskapstype ikke valgt");
  }

  const user = await createOrUpdateUser(
    {
      ...formData.contactInfo,
      customerTypeId: formData.onBehalfOf.otherPayer ? "1" : formData.customerType.id,
      discountCode: formData.accountType.discountCode?.code,
      mainCenterId: formData.mainCenter.id,
      marketingConsent: formData.summary.termsMarketing,
      termsAndConditions: formData.summary.termsAndConditions,
    },
    formData.user.accessToken,
    formData.organization?.id,
  );

  // lag medlemskapsordre for å kunne vise priser i oppsumering
  const order = await createOrder(
    formData.mainCenter.id,
    formData.customerType.id,
    formData.accountType.id,
    redirectUrl,
    dayjs(formData.contactInfo.birthdate).format("YYYY-MM-DD"),
    //In case of other payer we dont want the payers user to influence price calculation until onBehalfOf is registered
    formData.onBehalfOf.otherPayer ? undefined : user.accessToken,
  );
  return { order: order, user: user };
};

export function SummaryWrapper() {
  const router = useRouter();
  const pathName = usePathname();
  const redirectUrl = `${ROOT_URL}${pathName}`;
  const { removeItem } = storageUtils();
  const session = useSession();
  const isInitialized = useRef(false);

  const { formData, setFormData, validateForm } = useContext(FormStateContext);

  const [order, setOrder] = useState<OrderFragment | undefined>(undefined);
  const [errorCode] = useQueryState("error", parseAsString);

  const getOrder = useMemo(
    () => async () => {
      try {
        const result = await createOrderOnInitialLoad(formData, redirectUrl);
        setFormData({
          user: result.user,
        });
        setOrder(result.order);
      } catch (reason) {
        setServerSideError(typeof reason === "string" ? reason : "Kunne ikke opprette ordre");
        Logger.error(reason);
        return;
      } finally {
        setLoading(false);
      }
    },
    [formData, redirectUrl, setFormData],
  );

  const [serverSideError, setServerSideError] = useState<string | undefined>(undefined);

  const [validation, setValidation] = useState<Joi.ValidationResult | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const subscriptions = order?.items.filter((item: { __typename: string }) => item.__typename === "SubscriptionItem") as
    | Array<SubscriptionItem>
    | undefined;

  const subscription = subscriptions?.find((item) => item);

  const createUsersAndUpdateOrder = async () => {
    const user = await createOrUpdateUser(
      {
        ...formData.contactInfo,
        customerTypeId: formData.onBehalfOf.otherPayer ? "1" : (formData.customerType?.id ?? ""),
        discountCode: formData.accountType?.discountCode?.code,
        mainCenterId: formData.mainCenter?.id ?? "",
        marketingConsent: formData.summary.termsMarketing,
        termsAndConditions: formData.summary.termsAndConditions,
      },
      formData.user.accessToken,
      formData.organization?.id,
    );

    if (session.status !== "authenticated") {
      //login user
      const response = await signIn("brp-login", {
        password: user.password,
        redirect: false,
        username: formData.contactInfo.email,
      });
      if (response?.error) {
        throw new Error(response.error);
      }
    }

    //If other payer is selected we need to create or update other payer
    const otherUser = formData.onBehalfOf.otherPayer
      ? await createOrUpdateUser(
          {
            ...formData.onBehalfOf,
            customerTypeId: formData.customerType?.id ?? "1",
            mainCenterId: formData.mainCenter?.id ?? "",
            marketingConsent: formData.summary.termsMarketing,
            termsAndConditions: formData.summary.termsAndConditions,
          },
          formData.otherUser?.accessToken,
        )
      : undefined;

    if (order?.id && formData.accountType?.id) {
      const orderResponse = await updateOrder(
        user.accessToken,
        redirectUrl,
        order.id,
        formData.accountType.id,
        otherUser?.userId,
      );

      //redirect to payment
      if (orderResponse.paymentLink) {
        //analytics
        if (subscription) {
          sendStepEvent("oppsummering", 5, "oppsummering", formData.userJourney);
          await sendCheckoutEvent(formData, subscription, order);
        }
        router.push(orderResponse.paymentLink);
        removeItem(SessionStorage.ContactInfo, "SESSION");
        return;
      }
    }
    throw new Error("Order or account type not found");
  };

  //Kjører på første render og oppretter order slik at brukeren ser relevant pris i oppsummeringen
  useEffect(() => {
    if (isInitialized.current) {
      return;
    } // Prevent duplicate initialization
    isInitialized.current = true;

    getOrder();
  }, [getOrder]);

  useEffect(() => {
    if (errorCode) {
      const error = netsPaymentErrorsToString(errorCode);
      Logger.error(`BrpError: code: ${error}, description: ${error}`);
      setServerSideError(error);
    }

    setLoading(false);
  }, [errorCode]);

  // Håndterer hva som skjer når brukeren trykker "gå til betaling"
  const handleNext = async () => {
    setLoading(true);
    setServerSideError(undefined);

    //Om formen ikke passerer clientvalidering så stopper vi her
    const validation = validateForm(formData);
    if (validation.error) {
      setValidation(validation);
      setLoading(false);
      return;
    }

    try {
      //create or update user and user token
      await createUsersAndUpdateOrder();
    } catch (error) {
      setServerSideError(typeof error === "string" ? error : "Kunne ikke opprette ordre");
      Logger.error(error);
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  if (!order) {
    if (serverSideError) {
      return (
        <PageContent width="x-small">
          <ErrorMessage error={serverSideError} errorMessageFlavour={ErrorMessageFlavour.CREATE_MEMBERSHIP} />
        </PageContent>
      );
    }
    return <SummarySkeleton />;
  }

  return (
    <Summary
      subscription={subscription}
      order={order}
      loading={loading}
      handleNext={handleNext}
      validation={validation}
      serverSideError={serverSideError}
    />
  );
}
